import {fromJsonStrict, toJson, type JsonValue} from '@github-ui/dotcom-schema/protobuf'
import {
  AgentMergeActivityNotificationSchema,
  AgentMergeEvaluationScopeSchema,
  AgentMergeStatusResponseSchema,
  type AgentMergeEvaluationScope,
} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {useIsMutating, useQuery, useQueryClient} from '@github-ui/react-query'
import {sendCustomMetric} from '@github-ui/stats'
import {useAlive} from '@github-ui/use-alive'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {
  sameScope,
  type ActivityAnnouncementSource,
  type ActivityPresentation,
} from '../helpers/agent-merge-activity-reducer'
import {agentMergeActivityStore} from '../helpers/agent-merge-activity-store'
import {
  AgentMergeResolvedStatusError,
  hydrateResolvedStatus,
  presentResolvedStatus,
  type ResolvedStatusState,
} from '../helpers/agent-merge-resolved-status'
import {
  AgentMergeEvaluationScopeError,
  evaluationScopeKey,
  hasEvaluationStatusMarker,
  isEvaluationStatusInvalidation,
  validEvaluationScope,
} from '../helpers/agent-merge-status-validation'
import {agentMergeMutationKeyPrefix, agentMergeQueryKey} from '../page-data/loaders/use-agent-merge-page-data'

type StatusState = {
  resolved?: ResolvedStatusState
  activityChannel: string
  evaluationStatusChannel: string
}

function redactStatus(state: StatusState): StatusState {
  return {...state, resolved: undefined, activityChannel: '', evaluationStatusChannel: ''}
}

function denied(error: Error | null) {
  return isResponseError(error) && [401, 403, 404].includes(error.response.status)
}

function reportInvalidStatus() {
  sendCustomMetric({
    name: 'REACT_QUERY_SCHEMA_ERROR',
    value: 1,
    tags: {routeId: 'mergebox', queryName: 'agent_merge_evaluation_status'},
  })
}

export function useAgentMergeResolvedStatus({
  enabled,
  scope,
  configurationHeadOid,
  revision,
  configurationAvailable,
  configurationReadDenied = false,
  configurationEnabled,
  pullRequestChannel,
  viewerLogin,
}: {
  enabled: boolean
  scope?: AgentMergeEvaluationScope
  configurationHeadOid: string
  revision: string
  configurationAvailable: boolean
  configurationReadDenied?: boolean
  configurationEnabled: boolean
  pullRequestChannel?: string | null
  viewerLogin?: string | null
}) {
  const url = usePageDataUrl(PageData.agentMergeStatus)
  const configurationUrl = usePageDataUrl(PageData.agentMerge)
  const client = useQueryClient()
  const announcements = useMemo(() => agentMergeActivityStore(client, url), [client, url])
  const key = scope ? evaluationScopeKey(scope) : ''
  const serializedScope = scope ? toJson(AgentMergeEvaluationScopeSchema, scope) : undefined
  const viewer = viewerLogin ?? null
  const claimAnnouncement = useCallback(
    (identity: string, source: ActivityAnnouncementSource) =>
      announcements.claimAnnouncement(JSON.stringify([viewer, identity]), source),
    [announcements, viewer],
  )
  const queryKey = [PageData.agentMergeStatus, url, 'resolved', serializedScope, viewer] as const
  const mutating = useIsMutating({mutationKey: agentMergeMutationKeyPrefix(configurationUrl)}) > 0
  const active = enabled && !!scope && !configurationReadDenied
  const [now, setNow] = useState<number>()
  // Durable status lives on the server, not in persistent browser query storage.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister
  const query = useQuery<StatusState>({
    queryKey,
    enabled: active && configurationAvailable && !mutating,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    networkMode: 'online',
    queryFn: async ({signal}) => {
      if (!serializedScope) throw new AgentMergeEvaluationScopeError('Agent Merge evaluation scope is unavailable')
      const requestScope = fromJsonStrict(AgentMergeEvaluationScopeSchema, serializedScope)
      let readingResponse = true
      try {
        // Strict decoding is required even when general schema enforcement is off.
        // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema
        const json = await queryFnFetch<JsonValue>({queryDeps: {pathname: url, init: {signal}}})
        if (signal.aborted) throw signal.reason
        readingResponse = false
        const response = fromJsonStrict(AgentMergeStatusResponseSchema, json, {ignoreUnknownFields: true})
        if (!validEvaluationScope(requestScope) || !sameScope(response.context?.scope, requestScope.scope)) {
          throw new AgentMergeEvaluationScopeError('Agent Merge status does not match the current authorized scope')
        }
        if (!response.resolvedStatus) {
          throw new AgentMergeResolvedStatusError('Agent Merge status is missing its resolved primary')
        }
        return {
          resolved: hydrateResolvedStatus(response.resolvedStatus, requestScope),
          activityChannel: response.activityChannel,
          evaluationStatusChannel: response.evaluationStatusChannel,
        }
      } catch (error) {
        if (signal.aborted) throw signal.reason
        if (
          !isResponseError(error) &&
          !(error instanceof AgentMergeEvaluationScopeError) &&
          !(readingResponse && error instanceof TypeError)
        ) {
          reportInvalidStatus()
        }
        client.setQueriesData<StatusState>(
          {
            queryKey: [PageData.agentMergeStatus, url, 'resolved'],
            predicate: candidate => candidate.queryKey[4] === viewer,
          },
          previous => (previous ? redactStatus(previous) : undefined),
        )
        throw error
      }
    },
  })
  useEffect(() => {
    if (!configurationReadDenied && configurationAvailable) return
    const filter = {
      queryKey: [PageData.agentMergeStatus, url, 'resolved'],
      predicate: (candidate: {queryKey: readonly unknown[]}) => candidate.queryKey[4] === viewer,
    }
    void client.cancelQueries(filter)
    client.setQueriesData<StatusState>(filter, previous => (previous ? redactStatus(previous) : undefined))
  }, [client, configurationReadDenied, configurationAvailable, url, viewer])
  const {refetch} = query
  const refresh = useCallback(() => {
    if (active && configurationAvailable && !mutating && document.visibilityState === 'visible') {
      void refetch({cancelRefetch: false})
    }
  }, [active, configurationAvailable, mutating, refetch])
  // ETags wake an authoritative read without changing the request scope.
  useEffect(() => {
    refresh()
  }, [refresh, revision])
  const refreshConfiguration = useCallback(() => {
    if (client.isMutating({mutationKey: agentMergeMutationKeyPrefix(configurationUrl)}) === 0) {
      void client.invalidateQueries(
        {queryKey: agentMergeQueryKey(configurationUrl, configurationHeadOid)},
        {cancelRefetch: false},
      )
    }
  }, [client, configurationUrl, configurationHeadOid])
  const refreshOnFocus = useCallback(() => {
    setNow(Date.now())
    if (document.visibilityState !== 'visible') return
    refresh()
    refreshConfiguration()
  }, [refresh, refreshConfiguration])
  const checkExpiry = useCallback(() => {
    setNow(Date.now())
  }, [])

  useEffect(() => {
    if (active && query.error instanceof AgentMergeEvaluationScopeError) refreshConfiguration()
  }, [active, query.error, refreshConfiguration])
  useEffect(() => {
    if (enabled && !active) refreshConfiguration()
  }, [enabled, active, refreshConfiguration])
  useEffect(() => {
    if (active && query.data && !query.data.resolved && !query.isFetching) {
      refreshConfiguration()
    }
  }, [active, query.data, query.isFetching, refreshConfiguration])
  const deadline = query.data?.resolved?.deadline
  useEffect(() => {
    if (!active || deadline === undefined || !Number.isFinite(deadline)) return
    let timer: ReturnType<typeof setTimeout>
    const expire = () => {
      const currentTime = Date.now()
      const remaining = deadline - currentTime
      if (remaining > 0) {
        timer = setTimeout(expire, Math.ceil(remaining))
        return
      }
      setNow(currentTime)
      refresh()
    }
    timer = setTimeout(expire, Math.max(0, Math.ceil(deadline - Date.now())))
    return () => clearTimeout(timer)
  }, [active, key, deadline, refresh])

  const receive = useCallback(
    (data: JsonValue) => {
      if (!scope) return
      if (hasEvaluationStatusMarker(data)) {
        if (isEvaluationStatusInvalidation(data, scope)) refresh()
      } else {
        refresh()
      }
    },
    [scope, refresh],
  )
  const receiveActivity = useCallback(
    (data: JsonValue) => {
      let notification
      try {
        notification = fromJsonStrict(AgentMergeActivityNotificationSchema, data, {ignoreUnknownFields: true})
      } catch {
        reportInvalidStatus()
        return
      }
      if (sameScope(scope?.scope, notification.scope)) refresh()
    },
    [scope, refresh],
  )
  const authorized =
    active && configurationAvailable && !denied(query.error) && !(query.error instanceof AgentMergeEvaluationScopeError)
  const statusChannel = authorized ? query.data?.evaluationStatusChannel : null
  useAlive(statusChannel, receive, refreshOnFocus)
  useAlive(authorized && pullRequestChannel !== statusChannel ? pullRequestChannel : null, receive, refreshOnFocus)
  useAlive(authorized ? query.data?.activityChannel : null, receiveActivity, refreshOnFocus)
  useEffect(() => {
    if (!enabled) return
    window.addEventListener('focus', refreshOnFocus)
    window.addEventListener('online', refreshOnFocus)
    window.addEventListener('offline', checkExpiry)
    document.addEventListener('visibilitychange', refreshOnFocus)
    return () => {
      window.removeEventListener('focus', refreshOnFocus)
      window.removeEventListener('online', refreshOnFocus)
      window.removeEventListener('offline', checkExpiry)
      document.removeEventListener('visibilitychange', refreshOnFocus)
    }
  }, [enabled, checkExpiry, refreshOnFocus])

  const hasVisibleStatus = query.data?.resolved?.status.visible === true
  const isRefreshing =
    query.isPending ||
    (query.isFetching && (!query.data?.resolved || !query.isFetchedAfterMount)) ||
    (mutating && !hasVisibleStatus)
  const presentation: ActivityPresentation =
    !configurationEnabled && (mutating || query.isPending)
      ? {row: 'off'}
      : active && (!configurationAvailable || query.isError)
        ? {row: 'read_unavailable', attempt: key}
        : authorized && isRefreshing
          ? {row: 'refreshing'}
          : authorized && query.data?.resolved
            ? presentResolvedStatus(
                query.data.resolved,
                Math.max(now ?? 0, query.dataUpdatedAt),
                configurationAvailable && query.isFetchedAfterMount && (!mutating || hasVisibleStatus),
              )
            : {row: 'baseline'}
  return {
    presentation,
    claimAnnouncement,
    error: query.error,
    isPending: isRefreshing,
    isFetching: query.isFetching,
  }
}
